import { db } from "./firebase";
import {
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  getDocs,
  where,
  doc,
  updateDoc,
} from "firebase/firestore";

/**
 * Write a single activity event to Firestore.
 * Called from the client whenever a user does something notable.
 */
// Dedup guard: track recently written event keys to prevent double-writes
// (React StrictMode can double-invoke state updaters in dev)
const _recentEventKeys = new Set();

export async function writeActivityEvent({ type, userId, userName, userAvatar, message, meta = {} }) {
  if (!userId) return;

  // Build a dedup key from type + userId + message, valid for 2 seconds
  const dedupKey = `${type}|${userId}|${message}|${Math.floor(Date.now() / 2000)}`;
  if (_recentEventKeys.has(dedupKey)) return;
  _recentEventKeys.add(dedupKey);
  setTimeout(() => _recentEventKeys.delete(dedupKey), 2000);

  try {
    await addDoc(collection(db, "adminActivity"), {
      type,
      userId,
      userName: userName || "Unknown",
      userAvatar: userAvatar || "",
      message,
      meta,
      readByAdmin: false,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn("[adminActivity] write failed:", err?.code, err?.message);
  }
}

/**
 * Subscribe to the latest adminActivity events (real-time).
 * Returns an unsubscribe function.
 */
export function subscribeToAdminActivity(callback, maxItems = 100) {
  const q = query(
    collection(db, "adminActivity"),
    orderBy("createdAt", "desc"),
    limit(maxItems)
  );
  return onSnapshot(q, (snap) => {
    const events = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(events);
  }, (err) => console.error("subscribeToAdminActivity:", err));
}

/**
 * Mark all unread activity events as read.
 */
export async function markAllActivityRead() {
  try {
    const q = query(
      collection(db, "adminActivity"),
      where("readByAdmin", "==", false)
    );
    const snap = await getDocs(q);
    if (snap.empty) return;
    const batch = writeBatch(db);
    snap.docs.forEach((d) => {
      batch.update(doc(db, "adminActivity", d.id), { readByAdmin: true });
    });
    await batch.commit();
  } catch (err) {
    console.warn("markAllActivityRead failed:", err?.message);
  }
}

/**
 * Mark a single activity event as read.
 */
export async function markActivityRead(eventId) {
  try {
    await updateDoc(doc(db, "adminActivity", eventId), { readByAdmin: true });
  } catch (err) {
    console.warn("markActivityRead failed:", err?.message);
  }
}

/**
 * Human-readable "time ago" helper.
 */
export function timeAgo(timestamp) {
  if (!timestamp) return "";
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Emoji + label for each event type.
 */
export function activityMeta(type) {
  switch (type) {
    case "habit_completed":      return { emoji: "✅", label: "Habit" };
    case "weekly_challenge":     return { emoji: "⭐", label: "Weekly Challenge" };
    case "side_quest":           return { emoji: "✨", label: "Side Quest" };
    case "level_up":             return { emoji: "🎉", label: "Level Up" };
    case "badge_earned":         return { emoji: "🏅", label: "Badge" };
    case "streak_milestone":     return { emoji: "🔥", label: "Streak" };
    case "token_used":           return { emoji: "💎", label: "Token Used" };
    case "revive_used":          return { emoji: "💖", label: "Revive Used" };
    case "challenge_accepted":   return { emoji: "🎯", label: "Challenge Accepted" };
    case "challenge_claimed":    return { emoji: "🏆", label: "Challenge Claimed" };
    default:                     return { emoji: "📌", label: "Activity" };
  }
}
